import { useEffect } from "react";
import { Tab, Tabs } from "react-bootstrap";
import { useFormContext, useWatch } from "react-hook-form";
import { Table } from "stories";
import { currencyStyle, parseCurrency } from "utils/customHooks/currencyFormat";
import { getColumnTotal } from "../equityBreakdown";
import { generateFundsRows, numberify } from "./UsesTable";

const debtBreakdownHeader1 = [
  {
    id: "title",
    title: "Existing Debt",
    align: "left",
  },
  {
    id: "capital_stack",
    title: "% of Capital Stack",
    align: "right",
  },
  {
    id: "amount",
    title: "Requested Loan Amount",
    align: "right",
  },
];

const debtBreakdownHeader2 = [
  {
    id: "title",
    title: "Other Existing Debt",
    align: "left",
  },
  {
    id: "capital_stack",
    title: "% of Capital Stack",
    align: "right",
  },
  {
    id: "amount",
    title: "Amount",
    align: "right",
  },
];

const equityBreakdownHeader1 = [
  {
    id: "title",
    title: "Cash Equity",
    align: "left",
  },
  {
    id: "capital_stack",
    title: "% of Capital Stack",
    align: "right",
  },
  {
    id: "amount",
    title: "Amount",
    align: "right",
  },
];

export const SourcesOfFundsTable = ({
  equalCalculation,
  setEqualCalculation,
}) => {
  const form = useFormContext();
  const [funds, uses] = useWatch({
    control: form.control,
    name: ["funds", "uses"],
  });

  const tableRows = uses ? generateFundsRows(uses) : [];
  const mergedTableRows = [...tableRows, ...uses.other_uses];
  const totalUsesOfFunds = Number(
    mergedTableRows
      .reduce((prev, row) => {
        const amount = row.amount ? numberify(row.amount) : 0;
        if (!isNaN(Number(parseFloat(amount))))
          return prev + Number(parseFloat(amount));
        return prev;
      }, 0)
      .toFixed(2)
  );
  const debtBreakDownRows1 = [
    {
      uid: 1,
      title: "Requested Loan Amount",
      capital_stack: "",
      amount: `$ ${currencyStyle(funds.requested_loan)}`,
      props: {
        amount: {
          className: "fw-bold",
        },
      },
    },
  ];

  const debtBreakDownRows2 = funds.mortgages
    ? funds.mortgages.map((mortgage, i) => ({
        ...mortgage,
        uid: i + 1,
        capital_stack: "",
        amount: `$ ${currencyStyle(mortgage.amount)}`,
      }))
    : [];

  const totalDebt = Number(
    (
      getColumnTotal(debtBreakDownRows2, "amount") + funds.requested_loan
    ).toFixed(2)
  );
  const debtBreakDownRowsFooter = [
    {
      uid: "total-debt",
      title: "Total Debt",
      capital_stack: "",
      amount: `$ ${currencyStyle(totalDebt)}`,
    },
  ];

  const additionalCashEquity = Number(
    (totalUsesOfFunds - totalDebt - funds.total_cash_equity).toFixed(2)
  );

  const equityBreakdownRows1 = [
    {
      uid: "1",
      title: "Total Cash Equity (To Date)",
      capital_stack: "",
      amount: `$ ${currencyStyle(funds.total_cash_equity)}`,
    },
    {
      uid: "2-ad-re",
      title: "Additional Required Cash Equity",
      capital_stack: "",
      amount: `$ ${currencyStyle(additionalCashEquity)}`,
    },
  ];

  const totalEquity = Number(
    (additionalCashEquity + funds.total_cash_equity).toFixed(2)
  );

  const equityBreakdownFooter = [
    {
      uid: "1",
      title: "Total Cash Equity",
      capital_stack: "",
      amount: `$ ${currencyStyle(totalEquity)}`,
    },
  ];
  const arce = Math.abs(additionalCashEquity);
  const equityBreakdownFooter3 = [
    {
      uid: "ad-re-ce",
      title:
        additionalCashEquity > 0
          ? "Additional Required Cash Equity"
          : "Eligible Equity Takeout",
      capital_stack: "",
      amount: `$ ${currencyStyle(arce)}`,
      props: {
        title: {
          colSpan: 2,
        },
      },
    },
  ];

  const totalSourcesOfFunds = Number((totalEquity + totalDebt).toFixed(2));
  const equityBreakdownFooter2 = [
    {
      uid: "2",
      title: "Total Sources of Funds",
      capital_stack: "",
      amount: `$ ${currencyStyle(totalSourcesOfFunds)}`,
      props: {
        amount: {
          className: !equalCalculation ? "text-red" : "",
        },
      },
    },
  ];
  [
    ...debtBreakDownRows1,
    ...debtBreakDownRows2,
    ...debtBreakDownRowsFooter,
    ...equityBreakdownRows1,
    ...equityBreakdownFooter,
    ...equityBreakdownFooter2,
  ].forEach((row) => {
    let percent =
      (Number(parseCurrency(row.amount)) / totalSourcesOfFunds) * 100;
    percent = Number(percent.toFixed(2));
    row.capital_stack = `${currencyStyle(percent)}%`;
  });

  useEffect(() => {
    if (totalSourcesOfFunds !== totalUsesOfFunds && equalCalculation) {
      setEqualCalculation(false);
    } else if (totalSourcesOfFunds === totalUsesOfFunds && !equalCalculation) {
      setEqualCalculation(true);
    }
  }, [
    equalCalculation,
    setEqualCalculation,
    totalSourcesOfFunds,
    totalUsesOfFunds,
  ]);

  return (
    <Tabs activeKey="sources_of_funds">
      <Tab
        eventKey="sources_of_funds"
        title="Sources of Funds"
        tabClassName="text-left"
      >
        <div className="pr-2/5">
          <div className={"fw-bold mb-3"}>Debt Breakdown</div>
          <Table
            sameColumnWidth
            headers={debtBreakdownHeader1}
            rows={debtBreakDownRows1}
          />

          <div className="mt-3">
            <Table
              sameColumnWidth
              hideHeader={!!!funds.mortgages}
              headers={debtBreakdownHeader2}
              rows={debtBreakDownRows2}
              footer={debtBreakDownRowsFooter}
            />
          </div>

          <div className={"fw-bold mb-3 mt-3"}>Equity Breakdown</div>
          <div className="mt-3">
            <Table
              sameColumnWidth
              headers={equityBreakdownHeader1}
              rows={equityBreakdownRows1}
              footer={equityBreakdownFooter}
            />
          </div>
          <div className="mt-3">
            <Table
              hideHeader
              sameColumnWidth
              headers={equityBreakdownHeader1}
              rows={[]}
              footer={equityBreakdownFooter2}
            />
          </div>
          <hr className="mt-4 mb-4" />
          <div className="mt-3">
            <Table
              sameColumnWidth
              hideHeader
              headers={equityBreakdownHeader1}
              rows={[]}
              footer={equityBreakdownFooter3}
            />
          </div>
        </div>
      </Tab>
    </Tabs>
  );
};
